/**
 * Program Billing Activities Configuration Page
 *
 * Allows administrators to configure billing activities for programs
 */

import { useState, useEffect } from 'react';
import { ProgramBillingActivities } from '@/components/billing/ProgramBillingActivities';
import { getWeaveinApiUrl } from '@/src/config';
import { ChevronLeft, DollarSign } from 'lucide-react';
import CapabilityGate from '@/components/common/CapabilityGate';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface Program {
  id: number;
  name: string;
  description?: string;
  billing_model?: string;
}

export default function ProgramBillingPage() {
  const { t } = useAppTranslation();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [loading, setLoading] = useState(true);
  const organizationId = Number(localStorage.getItem('active_cbo_id')) || null;

  useEffect(() => {
    fetchPrograms();
  }, []);

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId })
    };
  };

  const fetchPrograms = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/programs'), {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProgram = (program: Program) => {
    setSelectedProgram(program);
  };

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader
          eyebrow="Admin · Billing"
          title={t('program-billing.program_billing_configuration', 'Program Billing Configuration')}
        />
        <PageContainer>
          <PageState
            variant="loading"
            title={t('program-billing.loading_programs', 'Loading programs...')}
          />
        </PageContainer>
      </div>
    );
  }

  if (selectedProgram) {
    return (
      <CapabilityGate organizationId={organizationId} capability="billing" behavior="show-upgrade" upgradeMessage="Billing is not enabled for your organization. Enable the Billing capability in Feature Capabilities to access program billing configuration.">
      <div className="flex h-full flex-col">
        <PageHeader
          eyebrow="Admin · Billing"
          title={t('program-billing.program_billing_configuration', 'Program Billing Configuration')}
          description={`Configure billing activities for ${selectedProgram.name}`}
          actions={
            <button
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
              onClick={() => setSelectedProgram(null)}
            >
              <ChevronLeft className="h-4 w-4" />
              {t('program-billing.back_to_programs', 'Back to Programs')}
            </button>
          }
        />
        <PageContainer>
          <ProgramBillingActivities
            programId={selectedProgram.id}
            programName={selectedProgram.name}
            maxUnitsPeriod={selectedProgram.max_units_period || 'month'}
            onUpdate={() => {}}
          />
        </PageContainer>
      </div>
      </CapabilityGate>
    );
  }

  return (
    <CapabilityGate organizationId={organizationId} capability="billing" behavior="show-upgrade" upgradeMessage="Billing is not enabled for your organization. Enable the Billing capability in Feature Capabilities to access program billing configuration.">
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Billing"
        title={t('program-billing.program_billing_configuration_1', 'Program Billing Configuration')}
        description={t('program-billing.select_a_program_to_configure', 'Select a program to configure billing activities')}
      />
      <PageContainer>
      {programs.length === 0 ? (
        <PageState
          variant="empty"
          title={t('program-billing.no_programs_found', 'No Programs Found')}
          message={t('program-billing.create_a_program_first_before', 'Create a program first before configuring billing activities.')}
        />
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {programs.map((program) => (
            <div
              key={program.id}
              className="cursor-pointer rounded-[10px] border border-v2border bg-surface p-5 shadow-v2-1 transition-shadow hover:border-v2accent hover:shadow-v2-2"
              onClick={() => handleSelectProgram(program)}
            >
              <h3 className="mb-1 font-display text-sm font-semibold tracking-[-0.01em] text-ink">
                {program.name}
              </h3>
              {program.description && (
                <p className="mb-4 text-[13px] text-ink-500">{program.description}</p>
              )}
              <div className="flex flex-col gap-2">
                <button className="btn btn-secondary" style={{ width: '100%' }}>
                  {t('program-billing.configure_billing_activities', 'Configure Billing Activities')}
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Navigate to PMPM billing page via the same mechanism app.tsx uses
                    localStorage.setItem('pmpm_program_filter', String(program.id));
                    localStorage.setItem('activeTab', 'pmpm-billing');
                    history.pushState({ tab: 'pmpm-billing' }, '', window.location.pathname);
                    window.dispatchEvent(new PopStateEvent('popstate', { state: { tab: 'pmpm-billing' } }));
                  }}
                >
                  <DollarSign className="h-4 w-4" style={{ display: 'inline' }} />
                  PMPM Billing
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </PageContainer>
    </div>
    </CapabilityGate>
  );
}
